import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import clsx from "clsx";

/* ─── Colour palette ──────────────────────────────────────────────────────── */
const C = {
  violet: { text: "text-violet-400", bg: "rgba(139,92,246,0.12)",  stroke: "#8b5cf6" },
  cyan:   { text: "text-cyan-400",   bg: "rgba(6,182,212,0.12)",   stroke: "#06b6d4" },
  green:  { text: "text-green-400",  bg: "rgba(34,197,94,0.12)",   stroke: "#22c55e" },
  yellow: { text: "text-yellow-400", bg: "rgba(251,191,36,0.12)",  stroke: "#fbbf24" },
  orange: { text: "text-orange-400", bg: "rgba(249,115,22,0.12)",  stroke: "#f97316" },
  red:    { text: "text-red-400",    bg: "rgba(239,68,68,0.12)",   stroke: "#ef4444" },
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function getLevel(xp) { return Math.floor((xp || 0) / 100); }

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() &&
         d.getMonth()    === n.getMonth()    &&
         d.getDate()     === n.getDate();
}

function isOlderThan7Days(dateStr) {
  if (!dateStr) return true;
  return (Date.now() - new Date(dateStr).getTime()) > 7 * 24 * 60 * 60 * 1000;
}

function relativeTime(dateStr) {
  if (!dateStr) return "Nie";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 1)   return "Gerade eben";
  if (mins  < 60)  return `vor ${mins} Min.`;
  if (hours < 24)  return `vor ${hours} Std.`;
  if (days  < 7)   return `vor ${days} Tag${days !== 1 ? "en" : ""}`;
  return new Date(dateStr).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function todayLabel() {
  return new Date().toLocaleDateString("de-DE", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function greeting() {
  const h = new Date().getHours();
  if (h < 11) return "Guten Morgen";
  if (h < 17) return "Guten Tag";
  return "Guten Abend";
}

/* ─── Inline SVG icons ────────────────────────────────────────────────────── */
function IcTeacher() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/>
      <path d="M8 21h8M12 17v4"/>
    </svg>
  );
}
function IcUsers() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
}
function IcPulse() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
    </svg>
  );
}
function IcZap() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );
}
function IcClipboard() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
      <line x1="9" y1="12" x2="15" y2="12"/>
      <line x1="9" y1="16" x2="12" y2="16"/>
    </svg>
  );
}
function IcChat() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
}
function IcTrophy() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M8 21h8M12 17v4M17 9H7V4h10v5z"/>
    </svg>
  );
}
function IcQuiz() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}
function IcWarning() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}
function IcArrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" strokeWidth="2.5"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
  );
}
function IcChevron() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}
function IcCalendar() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2"
      stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
}

/* ─── Skeleton loader ─────────────────────────────────────────────────────── */
function Skeleton({ className }) {
  return <div className={clsx("animate-pulse rounded-xl bg-white/5", className)} />;
}

function SkeletonCard() {
  return (
    <div className="bg-nexus-sf border border-nexus-border rounded-2xl p-4 space-y-3">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-7 w-16" />
      <Skeleton className="h-2.5 w-24" />
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 py-2.5 px-3">
      <Skeleton className="w-9 h-9 rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-2.5 w-20" />
      </div>
      <Skeleton className="h-3 w-12" />
    </div>
  );
}

/* ─── Stat card ───────────────────────────────────────────────────────────── */
function StatCard({ Icon, label, value, sub, color = "violet" }) {
  const c = C[color] || C.violet;
  return (
    <div
      className="relative overflow-hidden rounded-2xl border bg-nexus-sf group transition-all duration-300 hover:scale-[1.02] hover:shadow-lg p-4"
      style={{ borderColor: "rgba(255,255,255,0.07)" }}
    >
      {/* hover glow */}
      <div
        className="absolute top-0 right-0 w-20 h-20 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(circle at 100% 0%, ${c.stroke}20, transparent 70%)` }}
      />
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5">{label}</p>
          <p className={clsx("text-2xl font-black tabular-nums leading-none", c.text)}>{value}</p>
          {sub && <p className="text-[10px] text-gray-600 mt-1 truncate">{sub}</p>}
        </div>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: c.bg, color: c.stroke }}
        >
          <Icon />
        </div>
      </div>
    </div>
  );
}

/* ─── Activity row ────────────────────────────────────────────────────────── */
function ActivityRow({ student, onClick }) {
  const xp    = student.xp || 0;
  const level = getLevel(xp);
  return (
    <button
      onClick={onClick}
      className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 active:bg-white/8 transition-all duration-150 text-left"
    >
      {/* Avatar */}
      <div className="w-9 h-9 rounded-xl bg-nexus-s2 border border-nexus-border flex items-center justify-center text-lg flex-shrink-0 group-hover:border-violet-500/30 transition-colors">
        {student.user?.avatar || "🦊"}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate leading-tight">{student.user?.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-violet-400 font-bold">⚡ {xp.toLocaleString("de-DE")} XP</span>
          {(student.streak || 0) > 0 && (
            <span className="text-[10px] text-orange-400">🔥 {student.streak}</span>
          )}
          <span className="text-[10px] text-gray-600">Lv.{level}</span>
        </div>
      </div>
      {/* Time */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-gray-600"><IcCalendar /></span>
        <span className="text-[10px] text-gray-500">{relativeTime(student.user?.lastLoginAt)}</span>
      </div>
      <span className="text-gray-700 group-hover:text-gray-500 transition-colors flex-shrink-0"><IcChevron /></span>
    </button>
  );
}

/* ─── Top performer podium card ───────────────────────────────────────────── */
const MEDALS = ["🥇", "🥈", "🥉"];
const MEDAL_COLORS = [
  { border: "rgba(251,191,36,0.4)", bg: "rgba(251,191,36,0.06)", text: "#fbbf24" },
  { border: "rgba(156,163,175,0.4)", bg: "rgba(156,163,175,0.06)", text: "#9ca3af" },
  { border: "rgba(249,115,22,0.35)", bg: "rgba(249,115,22,0.06)", text: "#f97316" },
];

function TopPerformerRow({ student, rank }) {
  const xp     = student.xp || 0;
  const level  = getLevel(xp);
  const mc     = MEDAL_COLORS[rank] || MEDAL_COLORS[2];
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl transition-all duration-200 hover:bg-white/5"
      style={{ borderLeft: `2px solid ${mc.border}` }}
    >
      <span className="text-xl flex-shrink-0 w-7 text-center">{MEDALS[rank]}</span>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 border"
        style={{ background: mc.bg, borderColor: mc.border }}
      >
        {student.user?.avatar || "🦊"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">{student.user?.name}</p>
        <p className="text-[10px] text-gray-500">Level {level}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-sm font-black tabular-nums" style={{ color: mc.text }}>
          {xp.toLocaleString("de-DE")}
        </p>
        <p className="text-[9px] text-gray-600 uppercase tracking-wide">XP</p>
      </div>
    </div>
  );
}

/* ─── Needs attention card ────────────────────────────────────────────────── */
function NeedsAttentionRow({ student, onClick, reason }) {
  return (
    <button
      onClick={onClick}
      className="group w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-200 hover:border-orange-500/40 hover:bg-orange-500/5 active:scale-[0.99]"
      style={{ borderColor: "rgba(249,115,22,0.2)", background: "rgba(249,115,22,0.03)" }}
    >
      <div className="w-9 h-9 rounded-xl bg-nexus-s2 border flex items-center justify-center text-lg flex-shrink-0"
        style={{ borderColor: "rgba(249,115,22,0.25)" }}>
        {student.user?.avatar || "🦊"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">{student.user?.name}</p>
        <p className="text-[10px] text-orange-400/70 mt-0.5">{reason}</p>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-orange-400/70"><IcWarning /></span>
        <span className="text-orange-400 group-hover:translate-x-0.5 transition-transform"><IcChevron /></span>
      </div>
    </button>
  );
}

/* ─── Quick action pill ───────────────────────────────────────────────────── */
function QuickAction({ emoji, label, path, colorA, colorB, onClick }) {
  return (
    <button
      onClick={onClick}
      className="relative group overflow-hidden rounded-2xl p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.97]"
      style={{ background: `linear-gradient(135deg, ${colorA}, ${colorB})` }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.12), transparent 60%)" }}
      />
      <span className="block text-2xl mb-1.5">{emoji}</span>
      <p className="font-black text-sm text-white">{label}</p>
      <div className="absolute bottom-3 right-3 text-white/40 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all duration-200">
        <IcArrow />
      </div>
    </button>
  );
}

/* ─── Section header ──────────────────────────────────────────────────────── */
function SectionHeader({ Icon, title, badge, action }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="text-gray-400"><Icon /></span>
        <h3 className="font-black text-sm text-white">{title}</h3>
        {badge != null && (
          <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/25">
            {badge}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  TeacherDashboard                                                           */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function TeacherDashboard() {
  const { user }  = useAuthStore();
  const navigate  = useNavigate();
  const firstName = user?.name?.split(" ")[0] ?? "Lehrperson";

  /* ── Data fetching ────────────────────────────────────────────────────── */
  const { data: studentsData, isLoading: loadingStudents } = useQuery({
    queryKey: ["teacher", "students"],
    queryFn:  () => api.get("/students").then((r) => r.data),
    refetchInterval: 30_000,
  });

  const { data: tasksData, isLoading: loadingTasks } = useQuery({
    queryKey: ["teacher", "tasks"],
    queryFn:  () => api.get("/tasks").then((r) => r.data),
    refetchInterval: 60_000,
  });

  const { data: convData, isLoading: loadingConvs } = useQuery({
    queryKey: ["teacher", "conversations"],
    queryFn:  () => api.get("/messages/conversations").then((r) => r.data),
    refetchInterval: 20_000,
  });

  /* ── Derived data ─────────────────────────────────────────────────────── */
  const students = studentsData?.students || [];
  const tasks    = tasksData?.tasks       || [];
  const convos   = convData?.conversations || [];

  // Class stats
  const totalStudents = students.length;
  const activeToday   = students.filter((s) => isToday(s.user?.lastLoginAt)).length;
  const avgXP         = totalStudents > 0
    ? Math.round(students.reduce((a, s) => a + (s.xp || 0), 0) / totalStudents)
    : 0;
  const openTasks     = tasks.filter((t) => (t.completions?.pending || 0) > 0).length;

  // Activity feed — 8 most recently active
  const recentlyActive = [...students]
    .filter((s) => s.user?.lastLoginAt)
    .sort((a, b) => new Date(b.user.lastLoginAt) - new Date(a.user.lastLoginAt))
    .slice(0, 8);

  // Top performers — top 3 by XP
  const topPerformers = [...students]
    .sort((a, b) => (b.xp || 0) - (a.xp || 0))
    .slice(0, 3);

  // Needs attention — low XP, no streak, or inactive
  const needsAttention = students
    .map((s) => {
      const reasons = [];
      if ((s.xp || 0) < 50)          reasons.push("Wenig XP");
      if ((s.streak || 0) === 0)      reasons.push("Kein Streak");
      if (isOlderThan7Days(s.user?.lastLoginAt)) reasons.push("7+ Tage inaktiv");
      return { student: s, reasons };
    })
    .filter((item) => item.reasons.length > 0)
    .slice(0, 3);

  // Messages
  const unreadSum     = convos.reduce((a, c) => a + (c.unreadCount || 0), 0);
  const latestConvo   = convos[0] ?? null;

  const isLoading = loadingStudents || loadingTasks;

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5 animate-fade-in pb-10">

      {/* ── 1. HEADER ──────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border p-5"
        style={{
          background: "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(6,182,212,0.05) 100%)",
          borderColor: "rgba(139,92,246,0.2)",
        }}
      >
        {/* subtle top line */}
        <div className="absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(139,92,246,0.6), rgba(6,182,212,0.4), transparent)" }} />

        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Role badge */}
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full mb-3"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)" }}>
              <span style={{ color: "#8b5cf6" }}><IcTeacher /></span>
              <span className="text-[10px] font-black uppercase tracking-widest text-violet-300">Lehrperson</span>
            </div>

            <h1 className="text-xl font-black text-white leading-tight">
              {greeting()},<br />
              <span style={{ background: "linear-gradient(90deg, #8b5cf6, #06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                {firstName}!
              </span>
            </h1>

            <div className="flex items-center gap-1.5 mt-2 text-gray-500">
              <span className="text-gray-600"><IcCalendar /></span>
              <span className="text-[11px]">{todayLabel()}</span>
            </div>
          </div>

          {/* Live indicator */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/25">
              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              <span className="text-[10px] font-bold text-green-400">Live</span>
            </div>
            {!loadingStudents && (
              <div className="text-right">
                <p className="text-2xl font-black text-white tabular-nums">{activeToday}</p>
                <p className="text-[9px] text-gray-500 uppercase tracking-wide">heute aktiv</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── 2. CLASS STATS ─────────────────────────────────────────────── */}
      <section>
        <SectionHeader Icon={IcPulse} title="Klassen-Übersicht" />

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              Icon={IcUsers}
              label="Schüler gesamt"
              value={totalStudents}
              sub="registriert"
              color="violet"
            />
            <StatCard
              Icon={IcPulse}
              label="Heute aktiv"
              value={activeToday}
              sub={`von ${totalStudents}`}
              color="green"
            />
            <StatCard
              Icon={IcZap}
              label="Ø XP"
              value={avgXP.toLocaleString("de-DE")}
              sub="pro Schüler"
              color="cyan"
            />
            <StatCard
              Icon={IcClipboard}
              label="Offene Aufgaben"
              value={openTasks}
              sub="ausstehend"
              color={openTasks > 0 ? "orange" : "green"}
            />
          </div>
        )}
      </section>

      {/* ── 3. ACTIVITY FEED ───────────────────────────────────────────── */}
      <section>
        <SectionHeader
          Icon={IcPulse}
          title="Klassen-Aktivität"
          badge={recentlyActive.length}
          action={
            <button
              onClick={() => navigate("/students")}
              className="flex items-center gap-1 text-[10px] font-bold text-violet-400 hover:text-violet-300 transition-colors"
            >
              Alle anzeigen <IcArrow />
            </button>
          }
        />

        <div className="bg-nexus-sf border border-nexus-border rounded-2xl overflow-hidden divide-y divide-white/[0.04]">
          {loadingStudents ? (
            [...Array(5)].map((_, i) => <SkeletonRow key={i} />)
          ) : recentlyActive.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-4xl mb-2">😴</p>
              <p className="text-sm font-bold text-gray-500">Noch keine Aktivität</p>
              <p className="text-xs text-gray-600 mt-1">Schüler haben sich noch nicht eingeloggt</p>
            </div>
          ) : (
            recentlyActive.map((s) => (
              <ActivityRow
                key={s.id}
                student={s}
                onClick={() => navigate(`/students/${s.id}`)}
              />
            ))
          )}
        </div>
      </section>

      {/* ── 4. TOP PERFORMERS ──────────────────────────────────────────── */}
      {!loadingStudents && topPerformers.length > 0 && (
        <section>
          <SectionHeader Icon={IcTrophy} title="Top Performers" />

          <div
            className="rounded-2xl border overflow-hidden"
            style={{
              background: "linear-gradient(160deg, rgba(251,191,36,0.05) 0%, rgba(249,115,22,0.03) 100%)",
              borderColor: "rgba(251,191,36,0.15)",
            }}
          >
            {/* Subtle top accent */}
            <div className="h-px w-full"
              style={{ background: "linear-gradient(90deg, transparent, rgba(251,191,36,0.5), rgba(249,115,22,0.3), transparent)" }} />

            <div className="p-2 space-y-1">
              {topPerformers.map((s, i) => (
                <TopPerformerRow key={s.id} student={s} rank={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 5. NEEDS ATTENTION ─────────────────────────────────────────── */}
      {!loadingStudents && needsAttention.length > 0 && (
        <section>
          <SectionHeader
            Icon={IcWarning}
            title="Braucht Aufmerksamkeit"
            badge={needsAttention.length}
          />

          <div className="space-y-2">
            {needsAttention.map(({ student, reasons }) => (
              <NeedsAttentionRow
                key={student.id}
                student={student}
                reason={reasons.join(" · ")}
                onClick={() => navigate(`/students/${student.id}`)}
              />
            ))}
          </div>

          {/* Info note */}
          <p className="text-[10px] text-gray-600 mt-2 px-1">
            Schüler mit wenig XP, fehlendem Streak oder mehr als 7 Tagen Inaktivität.
          </p>
        </section>
      )}

      {/* ── 6. QUICK ACTIONS ───────────────────────────────────────────── */}
      <section>
        <SectionHeader Icon={IcZap} title="Schnellzugriff" />

        <div className="grid grid-cols-2 gap-2.5">
          <QuickAction emoji="📋" label="Aufgaben"   colorA="#8b5cf6" colorB="#6d28d9" onClick={() => navigate("/tasks")} />
          <QuickAction emoji="📝" label="Quizze"     colorA="#06b6d4" colorB="#0284c7" onClick={() => navigate("/quizzes")} />
          <QuickAction emoji="🏆" label="Challenges" colorA="#f97316" colorB="#dc2626" onClick={() => navigate("/challenges")} />
          <QuickAction emoji="💬" label="Nachrichten" colorA="#22c55e" colorB="#16a34a" onClick={() => navigate("/messages")} />
        </div>
      </section>

      {/* ── 7. RECENT MESSAGES ─────────────────────────────────────────── */}
      <section>
        <SectionHeader Icon={IcChat} title="Nachrichten" />

        <button
          onClick={() => navigate("/messages")}
          className="w-full group text-left"
        >
          <div
            className="relative overflow-hidden rounded-2xl border p-4 transition-all duration-200 hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-500/5 active:scale-[0.99]"
            style={{
              background: "linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(6,182,212,0.04) 100%)",
              borderColor: "rgba(124,58,237,0.2)",
            }}
          >
            {/* glow on hover */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{ background: "linear-gradient(135deg, rgba(124,58,237,0.04), transparent)" }} />

            <div className="flex items-start gap-3">
              {/* Icon */}
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(124,58,237,0.2)", border: "1px solid rgba(124,58,237,0.35)", color: "#a78bfa" }}
              >
                <IcChat />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-black text-sm text-white">
                    {loadingConvs ? "Laden…" : latestConvo ? latestConvo.name : "Keine Gespräche"}
                  </p>
                  {unreadSum > 0 && (
                    <span
                      className="flex-shrink-0 text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center text-white"
                      style={{ background: "#8b5cf6" }}
                    >
                      {unreadSum > 9 ? "9+" : unreadSum}
                    </span>
                  )}
                </div>
                {loadingConvs ? (
                  <Skeleton className="h-2.5 w-40 mt-1.5" />
                ) : latestConvo ? (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {latestConvo.lastMessage?.body ?? "Kein Inhalt"}
                  </p>
                ) : (
                  <p className="text-xs text-gray-600 mt-0.5">Starte eine Konversation</p>
                )}

                {/* Conversation count */}
                {!loadingConvs && convos.length > 0 && (
                  <div className="flex items-center gap-3 mt-2.5">
                    <span className="text-[10px] text-gray-600">{convos.length} Gespräch{convos.length !== 1 ? "e" : ""}</span>
                    {unreadSum > 0 && (
                      <span className="text-[10px] font-bold text-violet-400">{unreadSum} ungelesen</span>
                    )}
                  </div>
                )}
              </div>

              <span className="text-gray-600 group-hover:text-gray-400 group-hover:translate-x-0.5 transition-all duration-200 flex-shrink-0 mt-1">
                <IcChevron />
              </span>
            </div>
          </div>
        </button>
      </section>

    </div>
  );
}
